import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';

/**
 *  AZ ADMIN FELÜLET KATALÓGUSA – modulonként, egy helyen.
 *  ============================================================================
 *
 *  Az admin eddig CSAK az iskolai (SULINFORM) adminisztrációt tudta: a menüje a sablonba
 *  volt drótozva (11 gomb + legördülő, számozott opciókkal és egy `urlmatrix` táblával). Más
 *  modulban (Iktatgató, Könyvelés, Bér…) ugyanazt az iskolai menüt mutatta, a modul saját
 *  beállításai pedig nem voltak innen elérhetők.
 *
 *  Mostantól a menü ADAT: csoportok → menüpontok, modulonként, és egy ÁLTALÁNOS rész
 *  (jogosultság, rendszer), ami minden modulban megjelenik.
 *
 *   - SULINFORM: PONTOSAN a korábbi 11 csoport, ugyanazokkal a pontokkal és célokkal (a régi
 *     számozott opciók a feloldott útvonalukkal szerepelnek). Az általános részből csak az
 *     jelenik meg külön, ami az iskolai csoportokban még nincs benne (pl. Modulok témája).
 *   - Többi modul: a modul SAJÁT beállító képernyői + az általános rész. Iskolai képernyő
 *     (tanév, osztály, KRÉTA, bizonyítvány…) itt nem jelenik meg.
 *
 *  LÁTHATÓSÁG. Minden pontot a jogkörszerkesztő dönt el (`hozzaferesek().szabad(url)`, az
 *  AKTUÁLIS modul hozzáférés-listájából). Az url a pont útvonala query nélkül, vagy ha a jog
 *  kulcsa ettől eltér, a `jog` mező. A SULINFORM-on kívül a `*` NEM jelent admint (az
 *  iktatónak, a könyvelőnek, az ügyvédnek is csillaga van a saját modulján), ezért ott az
 *  általános rész pontjai a kiszolgáló admin-döntését is nézik (`szint`, lásd AdminSzintService).
 *
 *  Új admin-képernyő felvétele: ide egy sor a megfelelő csoportba, az útvonal az
 *  app-routing-ba, és ha jogot kell rá adni, a backend/jogosultsag.js BELSO_PONTOK-ba.
 */

export interface AdminElem {
  cim: string;
  /** Abszolút útvonal, query-vel együtt is (pl. '/admin/smtp-beallitas', '/naplo/torzsek?nezet=osa'). */
  ut: string;
  /** A jogkörszerkesztőben adható kulcs, ha nem az útvonal query nélküli része. */
  jog?: string;
  /** Rövid leírás – a (nem iskolai) admin kezdőlap csempéjén. */
  leiras?: string;
  /**
   *  A SULINFORM-on KÍVÜL milyen admin kell hozzá (a `*` ott nem admin):
   *   'modul'     – a modul adminja vagy az általános admin (pl. a modul jogkörei, témája),
   *   'altalanos' – csak az általános (iskolai `*` területű) admin (rendszerszintű beállítások).
   *  Ha nincs megadva, elég a jogkörszerkesztő joga.
   */
  szint?: 'modul' | 'altalanos';
  /** További keresőszavak – pl. a régi menünév, hogy a kereső a megszokott néven is megtalálja. */
  kulcsszo?: string;
  /** Csak ezekben a modulokban jelenik meg (pl. ami ügyfélkörhöz kötött). Ha nincs megadva, mindenhol. */
  modok?: string[];
}

/** A torzs_meta dinamikus táblái ezen a helyen jelennek meg a legördülőben. */
export interface AdminTorzsHely { torzsHely: true; }

export interface AdminCsoport {
  kulcs: string;
  cim: string;
  /** A gomb képe (assets/icon/…). */
  kep: string;
  /** Kezdőlap-ikon (ionicons). */
  ikon?: string;
  elemek: (AdminElem | AdminTorzsHely)[];
  /** A torzs_meta `menu_kategoria` értéke, ha a csoportba törzsadat-táblák is kerülnek. */
  torzsKategoria?: string;
  /** Legördülő helyett egyetlen gomb (pl. Kerettanterv). */
  kozvetlen?: AdminElem & { alcim: string };
  /** Üresen is látszik (a SULINFORM régi csoportjai mindig ott voltak). */
  uresenIs?: boolean;
}

export const TORZS: AdminTorzsHely = { torzsHely: true };

export function torzsHelyE(e: AdminElem | AdminTorzsHely): e is AdminTorzsHely {
  return (e as AdminTorzsHely).torzsHely === true;
}

// ── SULINFORM – a korábbi, bedrótozott menü hű átirata ─────────────────────────────────────────
//  A sorrend, a feliratok és a célok változatlanok. A régi számozott opciók (urlmatrix) itt a
//  feloldott útvonalukkal állnak: Intézmény 12 → etkezes, 13 → torzsadat; Dolgozók 4 → allomany;
//  Tanulók 3 → tanulo, 4 → szulo; Felület 6 → cert; Import 1/3/11/4/12/10/6/13 → importasc,
//  importtanulok, importszulok, importallomany, importtanmenet, importcsoport, importorarend,
//  importcsoporttagsag; Pénzügy 1–6 → nav-adatkapcsolat, checkout-fizetes, szamlatomb, pos,
//  storno-tomeges, cashbook-beallitas.
//
//  EGY VÁLTOZÁS: ami eddig útvonal-feloldás híján MINDENKINEK látszott (Alapértelmezett
//  intézmény, Mobil IdP-címtár, KRÉTA naplózás, KRÉTA e-Ügyintézés, Kerettanterv, a
//  törzsadat-táblák), az most a saját útvonalának jogát nézi – mind szerepel a jogkörszerkesztő
//  katalógusában, tehát kiosztható. Csillagos (admin) jogkörnél semmi nem változik.
const SULINFORM_CSOPORTOK: AdminCsoport[] = [
  {
    kulcs: 'intezmeny', cim: 'Intézmény', kep: 'assets/icon/icons8-settings-96.png', torzsKategoria: 'intezmeny', uresenIs: true,
    elemek: [
      { cim: 'Tanévváltás', ut: '/admin/tanevvalto' },
      { cim: 'Osztályok', ut: '/admin/osztaly-besorolas' },
      { cim: 'Csoportok', ut: '/admin/csoport-besorolas' },
      { cim: 'Étkezés', ut: '/admin/etkezes' },
      { cim: 'Törzsadatok', ut: '/admin/torzsadat' },
      { cim: 'Alapértelmezett intézmény', ut: '/admin/alapertelmezett-intezmeny' },
      { cim: 'Hiányzás igazolása', ut: '/admin/igazolas' },
      // A napló napi/heti zárása, kézi zárás/feloldás, a törölt beírt órák visszaállítása (naplo-zaras.js).
      { cim: 'Naplózárás', ut: '/admin/naplozaras' },
      // Az igazolatlan mulasztás értesítéseinek címzettjei, szülői értesítő (mulasztas-ertesites.js).
      { cim: 'Igazolatlan mulasztás – értesítések', ut: '/admin/mulasztas-ertesites' },
      // Az október 1-jei statisztika (OSAP 1410) táblái a rendszer adataiból – kitöltő segédlet PDF.
      { cim: 'KIR-STAT segédlet (okt. 1.)', ut: '/admin/kirstat' },
      // A jogviszony-események (be-/kiiratkozás, évfolyamváltás…) KIR-bejelentésének 5 napos határideje.
      { cim: 'KIR-bejelentések', ut: '/admin/kir-bejelentes' },
      // Iskolaváltó átadó csomag (Nkt. 41. § (8a)) és a másik iskolától jött csomag átvétele.
      { cim: 'Iskolaváltás – átadó csomag', ut: '/admin/tanulo-atado' },
      // A csengő-gép vezérlése: ki-be kapcsolás, azonnali csengetés, eltérő napok, eszközkulcs.
      { cim: 'Iskolai csengő', ut: '/admin/csengo' },
      TORZS,
    ],
  },
  {
    kulcs: 'dolgozok', cim: 'Dolgozók', kep: 'assets/icon/icons8-employee-96.png', torzsKategoria: 'dolgozok', uresenIs: true,
    elemek: [
      { cim: 'Állomány', ut: '/admin/allomany' },
      { cim: 'Bérek és pótlékok', ut: '/admin/ber' },
      { cim: 'Kinevezések', ut: '/admin/kinevezes' },
      { cim: 'Fiókok összevonása', ut: '/admin/allomany-osszevonas' },
      TORZS,
    ],
  },
  {
    kulcs: 'tanulok', cim: 'Tanulók', kep: 'assets/icon/icons8-student-96.png', torzsKategoria: 'tanulok', uresenIs: true,
    elemek: [
      { cim: 'Tanulók', ut: '/admin/tanulo' },
      { cim: 'Gondviselők', ut: '/admin/szulo' },
      TORZS,
      { cim: 'Osztályok', ut: '/admin/osztaly-besorolas' },
      { cim: 'Csoportok', ut: '/admin/csoport-besorolas' },
      { cim: 'Családok', ut: '/admin/csalad' },
    ],
  },
  {
    kulcs: 'felulet', cim: 'Felület', kep: 'assets/icon/icons8-database-administrator-96.png', torzsKategoria: 'felulet', uresenIs: true,
    elemek: [
      { cim: 'Tanúsítványkezelés', ut: '/admin/cert' },
      { cim: 'Időbélyeg-felülhitelesítés (bizonyítványok, nyomtatványok)', ut: '/admin/idobelyeg-felugyelet' },
      { cim: 'Nyomtatványok', ut: '/admin/nyomtatvany' },
      { cim: 'Záróértékelés', ut: '/admin/zaroertekeles' },
      { cim: 'Profilképek', ut: '/admin/microsoft-graph' },
      // Kulcsos JSON-címek a KRÉTA-exportok tartalmával, külső kimutatásnak – backend/adatexport.js.
      { cim: 'Adatexport (JSON)', ut: '/admin/adatexport', kulcsszo: 'kréta export bi json api kimutatás mulasztás jegy szülő tantárgyfelosztás' },
      // Adatvédelem: ki nézte meg a személyes adatokat (backend/hozzaferes-naplo.js), és kiknek az adatai
      // lépték túl a törvényi kezelési időt (backend/megorzes.js). Admin-terület: 'adatvedelem'.
      { cim: 'Hozzáférési napló', ut: '/admin/hozzaferes-naplo', kulcsszo: 'gdpr adatvédelem napló hozzáférés naplózás ki nézte audit' },
      { cim: 'Adatmegőrzés', ut: '/admin/adatmegorzes', kulcsszo: 'gdpr adatvédelem megőrzés lejárt törlés anonimizálás nkt 41' },
      // Az adatkezelési szabályzat / tájékoztató feltöltése a nyilvános /adatkezeles oldalra (backend/adatkezelesi-tajekoztato.js).
      { cim: 'Adatkezelési szabályzat', ut: '/admin/adatkezelesi-szabalyzat', kulcsszo: 'gdpr adatvédelem adatkezelési szabályzat tájékoztató nkt 43 közzététel' },
      { cim: 'Push-értesítés (app)', ut: '/admin/push-beallitas' },
      { cim: 'DÁP-csatlakozás (eAzonosítás)', ut: '/admin/dap-csatlakozas' },
      { cim: 'Entra-összerendelés', ut: '/admin/entra-parositas' },
      { cim: 'Fájltárolás', ut: '/admin/minio-beallitas' },
      { cim: 'Licenc', ut: '/admin/licenc' },
      { cim: 'Kimenő levelezés', ut: '/admin/smtp-beallitas' },
      { cim: 'Konrád nyuszi (segéd)', ut: '/admin/konrad-beallitas' },
      { cim: 'Hibajegy – Redmine kapcsolat', ut: '/admin/hibajegy-beallitas' },
      { cim: 'Adminisztrátori jogosultságok', ut: '/admin/jogosultak' },
      { cim: 'Jogkör-hozzárendelés', ut: '/admin/jogkorok' },
      { cim: 'Frissítés', ut: '/admin/frissites' },
      { cim: 'Belépési fiókok', ut: '/admin/keycloak-fiokok' },
      { cim: 'Mobil IdP-címtár', ut: '/admin/idp-regisztracio' },
      { cim: 'Kapcsolatok (kapcsolati kód)', ut: '/admin/kapcsolatok' },
      { cim: 'Jogosultságok', ut: '/admin/jogosultsag' },
      { cim: 'Menüszerkesztő', ut: '/admin/menuszerkeszto' },
      { cim: 'Wifi eszközök (MAC)', ut: '/admin/mac-allowlist' },
      { cim: 'Belépés más nevében', ut: '/admin/impersonalas' },
      { cim: 'Tudásbázis közzététele', ut: '/admin/tudasbazis' },
      TORZS,
    ],
  },
  {
    kulcs: 'import', cim: 'Import', kep: 'assets/icon/icons8-input-96.png', torzsKategoria: 'import', uresenIs: true,
    elemek: [
      // KIR-exportok (tanulók, gondviselők, alkalmazottak) egy helyen, előnézettel – kir-import.js.
      { cim: 'KIR-import varázsló', ut: '/admin/kir-import' },
      { cim: 'aSc XML', ut: '/admin/importasc' },
      { cim: 'Tanuló', ut: '/admin/importtanulok' },
      { cim: 'Gondviselő', ut: '/admin/importszulok' },
      { cim: 'Állomány', ut: '/admin/importallomany' },
      { cim: 'Tanmenet', ut: '/admin/importtanmenet' },
      // „Csoportok minden adata" (KRÉTA-export) – maguk a csoportok.
      { cim: 'Csoport', ut: '/admin/importcsoport' },
      // „Nem naplózott tanórák" (KRÉTA-export) → órarend, és a takarítás.
      { cim: 'Órarend', ut: '/admin/importorarend' },
      // Csoportba járó tanulók listája (KRÉTA-export) – tagság-import.
      { cim: 'Csoporttagság', ut: '/admin/importcsoporttagsag' },
      // Ugyanez fájl nélkül: a KRÉTA besorolás-képernyőiről közvetlenül.
      { cim: 'Csoporttagság a KRÉTÁ-ból (fájl nélkül)', ut: '/admin/kreta-csoport' },
      TORZS,
    ],
  },
  {
    kulcs: 'besorolas', cim: 'Besorolás', kep: 'assets/icon/icons8-vision-96.png', torzsKategoria: 'besorolas', uresenIs: true,
    elemek: [
      TORZS,
      { cim: 'Besorolás osztályba', ut: '/admin/osztaly-besorolas' },
      { cim: 'Besorolás csoportba', ut: '/admin/csoport-besorolas' },
      // KRÉTA-mintára: balra a forrás, jobbra a cél, a kijelöltek dátummal mennek át (osztályba és csoportba).
      { cim: 'Besorolás / átsorolás két táblával', ut: '/admin/besorolas-ket-tabla' },
      // Nem BE-, hanem ÁTsorolás – a régi besorolás lezárul, a hozzá tartozó csoportok is átkerülnek.
      { cim: 'Átsorolás (osztály + csoport)', ut: '/admin/atsorolas' },
      // Távozás: a besorolások lezárása + a mulasztási kimutatás az e-KRÉTA-ba átvezetéshez.
      { cim: 'Kiléptetés (távozó tanuló)', ut: '/admin/tanulo-kilepes' },
    ],
  },
  {
    kulcs: 'enaplo', cim: 'E-napló', kep: 'assets/icon/icons8-ebook-96.png', uresenIs: true,
    elemek: [
      { cim: 'KRÉTA naplózás', ut: '/admin/kreta-naploz' },
      // A KRÉTA e-Ügyintézésben beadott igazolások (EESZT és szülői) átemelése a naplóra.
      { cim: 'KRÉTA e-Ügyintézés (igazolások)', ut: '/admin/kreta-eugy' },
      { cim: 'KRÉTA közösségi szolgálat (szinkron)', ut: '/admin/iksz-szinkron' },
      { cim: 'KRÉTA feljegyzések (szinkron)', ut: '/admin/kreta-feljegyzes' },
      // Adatkörönként: mi megy át magától, mi kézi (félévi/év végi jegy), kihagyott/hibás tételek; önteszt.
      { cim: 'KRÉTA-átvitel állapota', ut: '/admin/kreta-atvitel', kulcsszo: 'kréta átvitel állapot önteszt kihagyott hibás félévi év végi jegy igazolás' },
      { cim: 'KRÉTA önteszt', ut: '/admin/kreta-onteszt', kulcsszo: 'kréta önteszt teszt működik belépés helyettesítés új tanuló ütemező szinkron hiba' },
      // A naplóhoz tartozó törzsadat-karbantartás: az értékelés-típus súlya és színe,
      // a két néven futó tantárgy összefésülése, a naptár- és feljegyzés-típusok.
      { cim: 'Értékelés-típusok (súly, szín)', ut: '/admin/ertekeles-tipus' },
      { cim: 'Tantárgyi felmentések', ut: '/admin/felmentes' },
      { cim: 'Fegyelmi eljárások', ut: '/admin/fegyelmi' },
      { cim: 'Tantárgy-összevonás', ut: '/admin/tantargy-osszevonas' },
      { cim: 'Naptártípusok (név, szín, ikon)', ut: '/admin/naptar-tipus' },
      { cim: 'Feljegyzés-típusok', ut: '/admin/feljegyzes-tipus' },
      // Tantermen kívüli, digitális munkarend (tanév rendje 5. §): elrendelt időszakok, visszavonás.
      { cim: 'Digitális munkarend (időszakok)', ut: '/admin/digitalis-munkarend' },
    ],
  },
  {
    //  ELLENŐRZÉS (2026-10-15, a user: „ez egy ellenőrző, nem osztályfőnöki feladat, külön menüre
    //  rakd és admin lássa”): az adminisztrátori ellenőrző kimutatások.
    kulcs: 'ellenorzes', cim: 'Ellenőrzés', kep: 'assets/icon/icons8-vision-96.png', uresenIs: true,
    elemek: [
      { cim: 'Jegy nélküli tantárgyak (tanárral)', ut: '/admin/jegy-ellenorzes' },
      { cim: 'Jegyek tanáronként', ut: '/admin/tanar-jegyek' },
      { cim: 'Könyveletlen órák', ut: '/admin/konyveletlen-orak', kulcsszo: 'be nem írt óra naplóellenőrzés könyvelés hiányzó tananyag' },
    ],
  },
  {
    //  HIVATALI KAPU – EGY HELYEN. A kapcsolat és a kiküldés az Iktatgató képernyője; innen oda
    //  navigálunk (egyetlen implementáció), a jog a saját útvonaluké.
    kulcs: 'hivatalikapu', cim: 'Hivatali kapu', kep: 'assets/icon/icons8-document-96.png', uresenIs: true,
    elemek: [
      { cim: 'Kapcsolat és kapcsolati kódok', ut: '/iktatgato/bksz' },
      { cim: 'Tömeges kiküldés (bérjegyzék)', ut: '/iktatgato/kuldes' },
      { cim: 'Beérkezett küldemények és igazolások', ut: '/iktatgato/kuldemenyek' },
      { cim: 'Kézbesítési napló (hol tart a küldemény)', ut: '/iktatgato/kezbesites' },
      { cim: 'Postakönyv (mi ment el)', ut: '/iktatgato/postakonyv' },
      { cim: 'Partnertörzs (címzettek)', ut: '/iktatgato/partnerek' },
    ],
  },
  {
    kulcs: 'dokumentumok', cim: 'Dokumentumok', kep: 'assets/icon/icons8-document-96.png', uresenIs: true,
    elemek: [
      { cim: 'Dokumentumgenerálás', ut: '/admin/doksigenerator' },
      { cim: 'Adatkinyerések', ut: '/admin/dokumentumok?nezet=export' },
      { cim: 'Bizonyítvány generálás', ut: '/admin/dokumentumok?nezet=generalas' },
      { cim: 'Bizonyítványmásodlat', ut: '/admin/bizonyitvany-masodlat', kulcsszo: 'másodlat elveszett megsemmisült névváltozás bizonyítvány pótlás' },
      { cim: 'Generált dokumentumok', ut: '/admin/dokumentumok?nezet=lista' },
      { cim: 'Archív átadás (bizonyítványok, jogutód)', ut: '/admin/dokumentumok?nezet=archiv' },
    ],
  },
  {
    kulcs: 'kerettanterv', cim: 'Kerettanterv', kep: 'assets/icon/icons8-copybook-96.png', elemek: [],
    kozvetlen: { cim: 'Kerettanterv', alcim: 'Tantárgysorrend', ut: '/admin/kerettanterv-sorrend' },
  },
  {
    kulcs: 'penzugy', cim: 'Pénzügy', kep: 'assets/icon/icons8-money-96.png', torzsKategoria: 'penzugy', uresenIs: true,
    elemek: [
      { cim: 'NAV adatkapcsolat', ut: '/admin/nav-adatkapcsolat' },
      { cim: 'Checkout fizetés', ut: '/admin/checkout-fizetes' },
      { cim: 'Számlatömbök', ut: '/admin/szamlatomb' },
      { cim: 'POS terminálok', ut: '/admin/pos' },
      { cim: 'Tömeges stornózás', ut: '/admin/storno-tomeges' },
      { cim: 'Cashbook beállítás', ut: '/admin/cashbook-beallitas' },
      TORZS,
    ],
  },
];

// ── A többi modul saját beállításai ──────────────────────────────────────────────────────────────
//  Ezek a modul saját képernyői (oda navigálunk), a jog is a modul saját url-je – ugyanaz, amit
//  a modul menüje is néz. MIRA, BILLIARD, HOUSEHOLD: nincs saját beállító képernyő, csak az
//  általános rész látszik.
const HIVATALI_KAPU: AdminCsoport = {
  kulcs: 'hivatalikapu', cim: 'Hivatali kapu', kep: 'assets/icon/icons8-document-96.png', ikon: 'mail-outline',
  elemek: [
    { cim: 'Kapcsolat és kapcsolati kódok', ut: '/iktatgato/bksz', leiras: 'BKSZ-kapcsolat, tanúsítvány, kapcsolati kódok' },
    { cim: 'Tömeges kiküldés (bérjegyzék)', ut: '/iktatgato/kuldes', leiras: 'Iratok kiküldése hivatali kapun' },
    { cim: 'Beérkezett küldemények és igazolások', ut: '/iktatgato/kuldemenyek', leiras: 'A postafiók letöltött tételei, érkeztetés és iktatás' },
    { cim: 'Kézbesítési napló (hol tart a küldemény)', ut: '/iktatgato/kezbesites', leiras: 'Feladási és letöltési igazolások a küldeményhez kötve' },
    { cim: 'Postakönyv (mi ment el)', ut: '/iktatgato/postakonyv', leiras: 'A kiküldött küldemények nyilvántartása' },
    { cim: 'Partnertörzs (címzettek)', ut: '/iktatgato/partnerek', leiras: 'Címzettek és kapcsolati kódjaik' },
  ],
};

const MODUL_CSOPORTOK: Record<string, AdminCsoport[]> = {
  SULINFORM: SULINFORM_CSOPORTOK,
  IKTATGATO: [
    {
      kulcs: 'iktatgato', cim: 'Iratkezelés', kep: 'assets/icon/icons8-filing-cabinet-96.png', ikon: 'library-outline',
      elemek: [
        { cim: 'Iktatókönyvek', ut: '/iktatgato/konyvek', leiras: 'Iktatókönyvek, fő- és alszámok, évnyitás' },
        { cim: 'Szabályok és jogosultság', ut: '/iktatgato/beallitasok', leiras: 'Intézményi iktatási szabályok, ki mit lát' },
      ],
    },
    HIVATALI_KAPU,
  ],
  NAPLO: [
    {
      kulcs: 'naplo-torzs', cim: 'Könyvelés', kep: 'assets/icon/icons8-general-ledger-96.png', ikon: 'albums-outline',
      elemek: [
        // A törzsadat-oldal kettős könyvelésű ügyfélnél maga irányít át a kettős nézetre.
        { cim: 'NAV technikai felhasználó (OSA)', ut: '/naplo/torzsek?nezet=osa', jog: '/naplo/torzsek',
          leiras: 'Az Online Számla kulcsai az aktív ügyfélhez' },
        { cim: 'Könyvelési beállítások (kettős)', ut: '/naplo/kettos?nezet=beallitas', jog: '/naplo/torzsek',
          leiras: 'Ellenszámlák, nyitó/záró számlák – kettős könyvelésnél' },
      ],
    },
    {
      kulcs: 'naplo-szerviz', cim: 'Szervíz', kep: 'assets/icon/icons8-sync-96.png', ikon: 'construct-outline',
      elemek: [
        { cim: 'Szinkronizálás', ut: '/naplo/szinkron', leiras: 'Automatikus lekérések időzítése' },
        { cim: 'Ügyfélkapu-fiókok', ut: '/naplo/fiokok', leiras: 'KAÜ-belépések és TOTP-kódok' },
        { cim: 'E-mail aláírás', ut: '/naplo/tanusitvany', leiras: 'Kimenő levelek aláíró tanúsítványa' },
        { cim: 'Év kiürítése', ut: '/admin/ev-kiurites', szint: 'modul',
          leiras: 'Egy ügyfél egy évének minden rekordja – mentéssel, visszaállítható' },
      ],
    },
  ],
  BER: [
    {
      kulcs: 'ber', cim: 'Bérszámfejtés', kep: 'assets/icon/icons8-cash-96.png', ikon: 'calculator-outline',
      elemek: [
        { cim: 'Beállítások', ut: '/ber/beallitasok', leiras: 'A bérszámfejtés paraméterei' },
        { cim: 'Év kiürítése', ut: '/admin/ev-kiurites', szint: 'modul',
          leiras: 'Egy ügyfél egy évének minden rekordja – mentéssel, visszaállítható' },
      ],
    },
  ],
  CAESAR: [
    {
      kulcs: 'caesar', cim: 'Ügykezelő', kep: 'assets/icon/icons8-folder-96.png', ikon: 'briefcase-outline',
      elemek: [
        { cim: 'Beállítások', ut: '/caesar/beallitasok', leiras: 'Az ügykezelő beállításai' },
        { cim: 'Ügytípusok', ut: '/caesar/ugytipusok', leiras: 'Ügytípusok és jellemzőik' },
        { cim: 'Sablonok', ut: '/caesar/sablonok', leiras: 'Irat- és beadványsablonok' },
      ],
    },
  ],
  WEBSHOP: [
    {
      kulcs: 'webshop', cim: 'Webáruház', kep: 'assets/icon/icons8-pos-96.png', ikon: 'storefront-outline',
      elemek: [
        { cim: 'Webshop oldalak', ut: '/webshop-admin', leiras: 'A webáruház oldalai és megjelenése' },
        { cim: 'Termékek és kategóriák', ut: '/webshop-admin/catalog', leiras: 'Termékkatalógus' },
      ],
    },
  ],
};

// ── Általános rész – minden modulban ─────────────────────────────────────────────────────────────
//  Modultól független (vagy a modult maga választó) képernyők. Iskolai képernyő NEM kerülhet ide.
const ALTALANOS_CSOPORTOK: AdminCsoport[] = [
  {
    kulcs: 'jogosultsag', cim: 'Jogosultság', kep: 'assets/icon/icons8-lock-96.png', ikon: 'shield-checkmark-outline',
    elemek: [
      // A szerkesztő az aktuális modult nyitja meg, és a modul jogkör-adminja is írhatja.
      { cim: 'Jogosultságok', ut: '/admin/jogosultsag', szint: 'modul', leiras: 'Jogkör × képernyő – ki mit láthat ebben a modulban' },
      // Felvétel belépési fiókkal + cégkiosztás (ügyfél / munkatárs); a Könyvelésből minden kapcsolt modulra – backend/felhasznalok.js.
      { cim: 'Felhasználók és cégek', ut: '/admin/felhasznalok', szint: 'modul', modok: ['NAPLO', 'BER', 'SZAMLAZO', 'INVENTARIO', 'ESZKOZ', 'GEPJARMU'],
        leiras: 'Új felhasználó belépési fiókkal, cégek kiosztása ügyfélnek és munkatársnak', kulcsszo: 'felhasználó felvétel ügyfél munkatárs cég' },
      { cim: 'Menüszerkesztő', ut: '/admin/menuszerkeszto', szint: 'modul', leiras: 'A bal oldali menü pontjai és sorrendje' },
      { cim: 'Jogkör-hozzárendelés', ut: '/admin/jogkorok', szint: 'altalanos', leiras: 'Ki milyen jogkörrel dolgozik, modulonként' },
      { cim: 'Adminisztrátori jogosultságok', ut: '/admin/jogosultak', szint: 'altalanos', leiras: 'Ki számít adminnak, és miért' },
      { cim: 'Belépés más nevében', ut: '/admin/impersonalas', szint: 'altalanos', leiras: 'A rendszer egy másik felhasználó szemével' },
    ],
  },
  {
    kulcs: 'rendszer', cim: 'Rendszer', kep: 'assets/icon/icons8-module-96.png', ikon: 'options-outline',
    elemek: [
      { cim: 'Modulok témája', ut: '/admin/modul-tema', szint: 'modul', leiras: 'Klasszikus vagy Fókusz kialakítás, modulonként' },
      { cim: 'Alapértelmezett intézmény', ut: '/admin/alapertelmezett-intezmeny', szint: 'modul', leiras: 'Melyik intézmény és modul nyíljon belépéskor' },
      { cim: 'Kimenő levelezés', ut: '/admin/smtp-beallitas', szint: 'altalanos', leiras: 'SMTP-kapcsolat, küldő cím' },
      { cim: 'Konrád nyuszi', ut: '/admin/konrad-beallitas', szint: 'modul', leiras: 'A beépített segéd: be/ki, helyi vagy Claude modell' },
      // A hibabejelentések Redmine-kapcsolata telepítésszintű, ezért csak az általános admin – backend/hibajegy.js.
      { cim: 'Hibajegy – Redmine kapcsolat', ut: '/admin/hibajegy-beallitas', szint: 'altalanos', leiras: 'Hová mennek a hibabejelentések' },
      { cim: 'Fájltárolás', ut: '/admin/minio-beallitas', szint: 'altalanos', leiras: 'MinIO kapcsolat' },
      { cim: 'Licenc', ut: '/admin/licenc', szint: 'altalanos', leiras: 'Az intézmény licencének aktiválása és állapota' },
      { cim: 'Belépési fiókok', ut: '/admin/keycloak-fiokok', szint: 'altalanos', leiras: 'Keycloak-fiókok, jelszó' },
      { cim: 'Mobil IdP-címtár', ut: '/admin/idp-regisztracio', szint: 'altalanos', leiras: 'Az intézmény a mobilos belépés választójában' },
      { cim: 'Kapcsolatok', ut: '/admin/kapcsolatok', szint: 'modul', leiras: 'Kapcsolati kód más intézménynek (pl. a könyvelőnek), napló, visszavonás' },
      // Minden modul adminja frissíthet (egy könyvelőirodában nincs iskolai admin) – backend/frissites.js.
      { cim: 'Frissítés', ut: '/admin/frissites', szint: 'modul', leiras: 'Új verzió telepítése' },
      { cim: 'Tudásbázis közzététele', ut: '/admin/tudasbazis', szint: 'altalanos', leiras: 'A felhasználói kézikönyv' },
    ],
  },
];

/**
 *  Az adott modul admin-menüje: a modul saját csoportjai, utánuk az általános rész. Az
 *  általános részből kimarad, ami a modul csoportjaiban már szerepel (SULINFORM-ban a legtöbb
 *  rendszerpont a „Felület" csoportban él) – így semmi nem jelenik meg kétszer.
 */
export function adminKatalogus(mode: string | null | undefined): AdminCsoport[] {
  const sajat = MODUL_CSOPORTOK[String(mode || '')] || [];
  const meglevo = new Set<string>();
  for (const cs of sajat) {
    for (const e of cs.elemek) if (!torzsHelyE(e)) meglevo.add(e.ut);
    if (cs.kozvetlen) meglevo.add(cs.kozvetlen.ut);
  }
  const modulba = (e: AdminElem | AdminTorzsHely) => torzsHelyE(e) || !e.modok || e.modok.includes(String(mode || ''));
  const altalanos = ALTALANOS_CSOPORTOK
    .map((cs) => ({ ...cs, elemek: cs.elemek.filter((e) => modulba(e) && (torzsHelyE(e) || !meglevo.has(e.ut))) }))
    .filter((cs) => cs.elemek.length);
  return [...sajat, ...altalanos];
}

const ALTALANOS_KULCSOK = new Set(ALTALANOS_CSOPORTOK.map((cs) => cs.kulcs));

/** Az általános (minden modulban megjelenő) részhez tartozik-e a csoport. */
export function altalanosCsoportE(cs: AdminCsoport): boolean {
  return ALTALANOS_KULCSOK.has(cs.kulcs);
}

/** A pont jogosultság-kulcsa: a `jog`, vagy az útvonal query és töredék nélkül. */
export function elemJog(e: AdminElem): string {
  return e.jog || e.ut.split('?')[0].split('#')[0];
}

export interface AdminLathatosag {
  szabad: (url: string) => boolean;
  /** SULINFORM-ban a `*` maga az admin – ott a szint nem számít (a régi viselkedés). */
  sulinform: boolean;
  /** A kiszolgáló döntése; `null` = nem tudjuk (hiba) → csak a jogkörszerkesztő dönt. */
  modulAdmin: boolean | null;
  altalanosAdmin: boolean | null;
}

export function elemLathato(e: AdminElem, l: AdminLathatosag): boolean {
  if (!l.szabad(elemJog(e))) return false;
  if (l.sulinform || !e.szint) return true;
  const admin = e.szint === 'altalanos' ? l.altalanosAdmin : l.modulAdmin;
  return admin !== false;
}

/**
 *  ADMIN-E A BELÉPŐ – a kiszolgáló szerint, modulra és általánosan.
 *
 *  A modul-téma végpontja (backend/modul-tema.js) pont ezt adja vissza: `admin` = a modul
 *  adminja VAGY az általános admin; SULINFORM-ra kérdezve csak az általános admin. Hiba esetén
 *  `null`: a menü ilyenkor csak a jogkörszerkesztőre hallgat (a képernyők kiszolgálója úgyis
 *  maga dönt – ez a szűrés a felhasználónak szól, nem védelem).
 */
@Injectable({ providedIn: 'root' })
export class AdminSzintService {
  private tar = new Map<string, Promise<boolean | null>>();

  constructor(private http: HttpClient, private data: DataService) {}

  private kerdez(fe: string, mode: string): Promise<boolean | null> {
    const kulcs = `${fe}|${mode}`;
    if (!this.tar.has(kulcs)) {
      this.tar.set(kulcs, firstValueFrom(
        this.http.get<any>(`/api/modul-tema/${fe}?mode=${encodeURIComponent(mode)}`))
        .then((r) => (typeof r?.admin === 'boolean' ? r.admin : null))
        .catch(() => { this.tar.delete(kulcs); return null; }));
    }
    return this.tar.get(kulcs)!;
  }

  async szintek(mode: string): Promise<{ modulAdmin: boolean | null; altalanosAdmin: boolean | null }> {
    const fe = String((await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '').trim();
    if (!fe || !mode) return { modulAdmin: null, altalanosAdmin: null };
    const [modulAdmin, altalanosAdmin] = await Promise.all([
      this.kerdez(fe, mode), this.kerdez(fe, 'SULINFORM')]);
    return { modulAdmin, altalanosAdmin };
  }
}


// ── TERÜLETEK – az admin kezelőközpontja (2026-10-16) ───────────────────────────────────────────
//  A felső, három sorba tördelődő legördülő-sor helyett (a user: „nagyon sok menünk kezd lenni”)
//  a bal oldalon TERÜLETEK, a kiválasztott terület pontjai alcsoportokban, kártyán, rövid
//  leírással; felül kereső. SULINFORM-ban a 13 régi csoport ~100 pontja 8 területbe rendeződik
//  (ugyanazok az útvonalak és jogok – csak a csoportosítás új; a régi menünév a kulcsszóban, hogy
//  a kereső azon is megtalálja). Más modulban minden régi csoport egy terület.
//  A területek képe a régi gombok icons8-ikonjaiból való (assets/icon/).

export interface AdminAlcsoport {
  cim: string;
  elemek: (AdminElem | AdminTorzsHely)[];
  /** A `TORZS` helyén ezeknek a torzs_meta kategóriáknak a táblái jelennek meg. */
  torzsKategoriak?: string[];
}

export interface AdminTerulet {
  kulcs: string;
  cim: string;
  leiras: string;
  kep: string;
  alcsoportok: AdminAlcsoport[];
}

const p = (cim: string, ut: string, leiras: string, tobb: Partial<AdminElem> = {}): AdminElem => ({ cim, ut, leiras, ...tobb });
const torzsTablak = (...kategoriak: string[]): AdminAlcsoport => ({ cim: 'Törzsadat-táblák', elemek: [TORZS], torzsKategoriak: kategoriak });

const SULINFORM_TERULETEK: AdminTerulet[] = [
  {
    kulcs: 'intezmeny', cim: 'Intézmény és tanév', kep: 'assets/icon/icons8-school-96.png',
    leiras: 'Az iskola alapadatai, a tanév rendje és a törzsadatok.',
    alcsoportok: [
      { cim: 'Tanév', elemek: [
        p('Tanévváltás', '/admin/tanevvalto', 'Tanév lezárása, új tanév előkészítése, osztályok léptetése'),
        p('Naplózárás', '/admin/naplozaras', 'Napi vagy heti zárás, kézi zárás és feloldás'),
        p('Iskolai csengő', '/admin/csengo', 'Csengetési rend, azonnali csengetés, eltérő napok'),
      ] },
      { cim: 'Törzsadatok', elemek: [
        p('Törzsadatok', '/admin/torzsadat', 'A törzsadat-táblák szerkesztése'),
        p('Kerettanterv', '/admin/kerettanterv-sorrend', 'Tantárgysorrend a kerettanterv szerint', { kulcsszo: 'tantárgysorrend' }),
        p('Étkezés', '/admin/etkezes', 'Beszállítók, étkezőhelyek, menük'),
        p('Alapértelmezett intézmény', '/admin/alapertelmezett-intezmeny', 'Melyik intézmény és modul nyíljon belépéskor'),
      ] },
      { cim: 'Statisztika', elemek: [
        p('KIR-STAT segédlet', '/admin/kirstat', 'OSAP 1410 – az október 1-jei állapot táblái', { kulcsszo: 'okt. 1. statisztika' }),
        p('KIR-bejelentések', '/admin/kir-bejelentes', 'Jogviszony-változások 5 napos KIR-bejelentése, „Bejelentve” jelölés', { kulcsszo: 'KIR jogviszony beiratkozás kiiratkozás' }),
      ] },
      torzsTablak('intezmeny'),
    ],
  },
  {
    kulcs: 'emberek', cim: 'Emberek', kep: 'assets/icon/icons8-group-96.png',
    leiras: 'Tanulók, gondviselők, családok és munkatársak adatai.',
    alcsoportok: [
      { cim: 'Tanulók és családok', elemek: [
        p('Tanulók', '/admin/tanulo', 'Tanulói törzsadatok, jogviszony, SNI/BTMN'),
        p('Gondviselők', '/admin/szulo', 'Szülői adatok és kapcsolatok', { kulcsszo: 'szülők' }),
        p('Családok', '/admin/csalad', 'Testvérek, családi egyenleg'),
        p('Iskolaváltás – átadó csomag', '/admin/tanulo-atado', 'Tanulói adatok átadása / átvétele iskolaváltáskor (JSON, PDF)', { kulcsszo: 'átadás átvétel másik iskola jegyek' }),
        p('Adatbank', '/adatbank', 'Szabad keresés és kész listák (osztálynévsor, elérhetőségek)'),
      ] },
      { cim: 'Munkatársak', elemek: [
        p('Állomány', '/admin/allomany', 'Munkatársak és feladatellátás', { kulcsszo: 'dolgozók' }),
        p('Bérek és pótlékok', '/admin/ber', 'Bér érvényességi időszakokkal, pótlékok'),
        p('Kinevezések', '/admin/kinevezes', 'Osztályfőnöki és vezetői kinevezések'),
        p('Fiókok összevonása', '/admin/allomany-osszevonas', 'Kettőzött munkatársi fiókok egyesítése'),
      ] },
      torzsTablak('tanulok', 'dolgozok'),
    ],
  },
  {
    kulcs: 'besorolas', cim: 'Osztályok és besorolás', kep: 'assets/icon/icons8-class-96.png',
    leiras: 'Osztályok, csoportok, be- és átsorolás, kiléptetés.',
    alcsoportok: [
      { cim: 'Osztályok és csoportok', elemek: [
        p('Osztályok', '/admin/osztaly-besorolas', 'Osztályok és a tanulók osztályba sorolása', { kulcsszo: 'besorolás osztályba' }),
        p('Csoportok', '/admin/csoport-besorolas', 'Csoportok, tagság, tantárgy-társítás', { kulcsszo: 'besorolás csoportba' }),
      ] },
      { cim: 'Átsorolás', elemek: [
        p('Be- és átsorolás két táblával', '/admin/besorolas-ket-tabla', 'Balra a forrás, jobbra a cél, dátummal'),
        p('Átsorolás (osztály + csoport)', '/admin/atsorolas', 'A régi besorolás lezárul, a csoportok is átkerülnek'),
        p('Kiléptetés', '/admin/tanulo-kilepes', 'Távozó tanuló: besorolások lezárása, mulasztási kimutatás'),
      ] },
      torzsTablak('besorolas'),
    ],
  },
  {
    kulcs: 'naplo', cim: 'Napló és értékelés', kep: 'assets/icon/icons8-ebook-96.png',
    leiras: 'A napló működésének beállításai, a tanulói kivételek és az ellenőrzések.',
    alcsoportok: [
      { cim: 'Beállítások', elemek: [
        p('Értékelés-típusok', '/admin/ertekeles-tipus', 'Az értékelés-típusok súlya és színe'),
        p('Feljegyzés-típusok', '/admin/feljegyzes-tipus', 'A feljegyzések fajtái és mezői'),
        p('Naptártípusok', '/admin/naptar-tipus', 'Név, szín, ikon'),
        p('Digitális munkarend', '/admin/digitalis-munkarend', 'Elrendelt időszakok osztályra, évfolyamra'),
        p('Igazolatlan mulasztás – értesítések', '/admin/mulasztas-ertesites', 'Ki kapja: szülői értesítő, osztályfőnök, igazgató, adminok, név szerint',
          { kulcsszo: 'mulasztás igazolatlan értesítés szülő küszöb címzett riasztás' }),
      ] },
      { cim: 'Tanulói kivételek', elemek: [
        p('Tantárgyi felmentések', '/admin/felmentes', 'Felmentések és határozatok'),
        p('Tantárgy-összevonás', '/admin/tantargy-osszevonas', 'Két néven futó tantárgy összefésülése'),
        p('Hiányzás igazolása', '/admin/igazolas', 'Szülői napkeret és igazgatói engedélyek'),
      ] },
      { cim: 'Fegyelmi', elemek: [
        p('Fegyelmi eljárások', '/admin/fegyelmi', 'Egyeztetés, megindítás, tárgyalás, határozat – a kötelező értesítéssel', { kulcsszo: 'határozat egyeztető eljárás' }),
      ] },
      { cim: 'Ellenőrzés', elemek: [
        p('Jegy nélküli tantárgyak', '/admin/jegy-ellenorzes', 'Kinek nincs még jegye – tanárral, CSV-be'),
        p('Jegyek tanáronként', '/admin/tanar-jegyek', 'Ki milyen jegyeket adott – tantárgyanként, átlaggal, PDF/CSV', { kulcsszo: 'tanár szerinti értékelés lista jegyek átlag' }),
        p('Könyveletlen órák', '/admin/konyveletlen-orak', 'Kinek milyen órája nincs még beírva a naplóba – PDF-be',
          { kulcsszo: 'be nem írt óra naplóellenőrzés könyvelés hiányzó tananyag' }),
        p('Lemorzsolódással veszélyeztetett tanulók', '/admin/lemorzsolodas', 'Korai jelzés (44/J. §), febr. 10. / jún. 30. adatszolgáltatás', { kulcsszo: 'lemorzsolódás veszélyeztetett HH HHH hátrányos helyzet korai jelző' }),
      ] },
    ],
  },
  {
    kulcs: 'dokumentumok', cim: 'Dokumentumok és bizonyítvány', kep: 'assets/icon/icons8-certificate-96.png',
    leiras: 'Bizonyítvány, kötelező nyomtatványok, a tárolt iratok és hitelességük.',
    alcsoportok: [
      { cim: 'Bizonyítvány', elemek: [
        p('Bizonyítvány generálás', '/admin/dokumentumok?nezet=generalas', 'Aláírt, időbélyeges bizonyítvány és törzslap'),
        p('Bizonyítványmásodlat', '/admin/bizonyitvany-masodlat', 'Elveszett, megsemmisült bizonyítvány helyett, névváltozáskor (EMMI r. 113. §)',
          { kulcsszo: 'másodlat elveszett megsemmisült névváltozás bizonyítvány pótlás' }),
        p('Záróértékelés', '/admin/zaroertekeles', 'Félévi és év végi értékelés beállításai'),
      ] },
      { cim: 'Dokumentumok', elemek: [
        p('Adatkinyerések', '/admin/dokumentumok?nezet=export', 'Exportok és a kötelező nyomtatványok (beírási napló, osztálynapló, értesítő…)'),
        p('Generált dokumentumok', '/admin/dokumentumok?nezet=lista', 'Minden tárolt, kiadott irat'),
        p('Dokumentumgenerálás', '/admin/doksigenerator', 'Iratok sablonból (pl. kinevezésmódosítás)'),
        p('Nyomtatványok', '/admin/nyomtatvany', 'Nyomtatvány-sablonok'),
        p('Archív átadás', '/admin/dokumentumok?nezet=archiv', 'Bizonyítványok átadása a fenntartónak, jogutódnak (80 év)'),
      ] },
      { cim: 'Hitelesség', elemek: [
        p('Tanúsítványkezelés', '/admin/cert', 'Aláíró tanúsítványok'),
        p('Időbélyeg-felülhitelesítés', '/admin/idobelyeg-felugyelet', 'Bizonyítványok, törzslapok, nyomtatványok megőrzése'),
      ] },
    ],
  },
  {
    kulcs: 'adatcsere', cim: 'Adatcsere', kep: 'assets/icon/icons8-sync-96.png',
    leiras: 'Importok, a KRÉTA-kapcsolat és a hivatali kapu.',
    alcsoportok: [
      { cim: 'Import', elemek: [
        p('KIR-import varázsló', '/admin/kir-import', 'KIR-exportok (tanulók, gondviselők, alkalmazottak) előnézettel'),
        p('aSc XML', '/admin/importasc', 'Órarend, tantárgyfelosztás, termek'),
        p('Órarend', '/admin/importorarend', 'KRÉTA „Nem naplózott tanórák” exportból', { kulcsszo: 'import' }),
        p('Tanmenet', '/admin/importtanmenet', 'Tanmenetek betöltése', { kulcsszo: 'import' }),
        p('Csoport', '/admin/importcsoport', 'KRÉTA „Csoportok minden adata” exportból', { kulcsszo: 'import' }),
        p('Csoporttagság', '/admin/importcsoporttagsag', 'KRÉTA csoporttagság-exportból', { kulcsszo: 'import' }),
        p('Tanuló (egyedi import)', '/admin/importtanulok', 'Tanulói adatok betöltése fájlból', { kulcsszo: 'import' }),
        p('Gondviselő (egyedi import)', '/admin/importszulok', 'Szülői adatok betöltése fájlból', { kulcsszo: 'import' }),
        p('Állomány (egyedi import)', '/admin/importallomany', 'Munkatársak betöltése fájlból', { kulcsszo: 'import' }),
      ] },
      { cim: 'KRÉTA', elemek: [
        p('KRÉTA naplózás', '/admin/kreta-naploz', 'Az órák beírása a KRÉTÁ-ba'),
        p('KRÉTA e-Ügyintézés', '/admin/kreta-eugy', 'EESZT- és szülői igazolások átemelése a naplóra', { kulcsszo: 'igazolások' }),
        p('KRÉTA közösségi szolgálat', '/admin/iksz-szinkron', 'Szinkron a KRÉTÁ-val'),
        p('KRÉTA feljegyzések', '/admin/kreta-feljegyzes', 'Szinkron a KRÉTÁ-val'),
        p('KRÉTA-átvitel állapota', '/admin/kreta-atvitel', 'Mi megy át magától, mi kézi, mi akadt el; kapcsolat-önteszt',
          { kulcsszo: 'kréta átvitel állapot önteszt kihagyott hibás félévi év végi jegy igazolás' }),
        p('KRÉTA önteszt', '/admin/kreta-onteszt', 'Működik-e funkciónként: belépés, naplózás, helyettesítés, jegy, mulasztás, tagság, tanulói adat, új tanuló'),
        p('Csoporttagság a KRÉTÁ-ból', '/admin/kreta-csoport', 'Fájl nélkül, a KRÉTA besorolás-képernyőiről'),
      ] },
      { cim: 'Hivatali kapu', elemek: HIVATALI_KAPU.elemek },
      torzsTablak('import'),
    ],
  },
  {
    kulcs: 'penzugy', cim: 'Pénzügy', kep: 'assets/icon/icons8-money-96.png',
    leiras: 'Számlázás, fizetés, NAV-kapcsolat.',
    alcsoportok: [
      { cim: 'Számlázás', elemek: [
        p('Számlatömbök', '/admin/szamlatomb', 'Sorszámtartományok'),
        p('NAV adatkapcsolat', '/admin/nav-adatkapcsolat', 'Online számla technikai beállítás'),
        p('Tömeges stornózás', '/admin/storno-tomeges', 'Számlák visszavonása'),
      ] },
      { cim: 'Fizetés', elemek: [
        p('Checkout fizetés', '/admin/checkout-fizetes', 'Bankkártyás fizetés beállítása'),
        p('POS terminálok', '/admin/pos', 'Kártyaterminálok'),
        p('Cashbook beállítás', '/admin/cashbook-beallitas', 'Pénztárkönyv'),
      ] },
      torzsTablak('penzugy'),
    ],
  },
  {
    kulcs: 'rendszer', cim: 'Rendszer és jogosultság', kep: 'assets/icon/icons8-database-administrator-96.png',
    leiras: 'Ki mit lát, a külső kapcsolatok és a rendszer működése.',
    alcsoportok: [
      { cim: 'Jogosultság', elemek: [
        p('Jogosultságok', '/admin/jogosultsag', 'Jogkör × képernyő – ki mit láthat'),
        p('Jogkör-hozzárendelés', '/admin/jogkorok', 'Ki milyen jogkörrel dolgozik'),
        p('Adminisztrátori jogosultságok', '/admin/jogosultak', 'Ki számít adminnak, melyik területen'),
        p('Menüszerkesztő', '/admin/menuszerkeszto', 'A bal oldali menü pontjai és sorrendje'),
        p('Belépési fiókok', '/admin/keycloak-fiokok', 'Keycloak-fiókok, jelszó'),
        p('Belépés más nevében', '/admin/impersonalas', 'A rendszer egy másik felhasználó szemével'),
      ] },
      { cim: 'Integrációk', elemek: [
        p('DÁP-csatlakozás', '/admin/dap-csatlakozas', 'eAzonosítás: tanúsítványkérelmek a SZEÜSZ-höz'),
        p('Entra-összerendelés', '/admin/entra-parositas', 'Microsoft-fiókok párosítása'),
        p('Push-értesítés (app)', '/admin/push-beallitas', 'Értesítés az appra (Android, iOS)'),
        p('Profilképek', '/admin/microsoft-graph', 'Microsoft Graph szinkron'),
        p('Adatexport (JSON)', '/admin/adatexport', 'KRÉTA-exportok kulcsos JSON-címen, kimutatásnak (BI)',
          { kulcsszo: 'kréta export bi json api kimutatás mulasztás jegy szülő tantárgyfelosztás' }),
        p('Hozzáférési napló', '/admin/hozzaferes-naplo', 'Ki, mikor, melyik személyes adatot kérte le (GDPR 32. cikk)',
          { kulcsszo: 'gdpr adatvédelem napló hozzáférés naplózás ki nézte audit' }),
        p('Adatmegőrzés', '/admin/adatmegorzes', 'Kiknek az adatai lépték túl a törvényi kezelési időt (Nkt. 41. §)',
          { kulcsszo: 'gdpr adatvédelem megőrzés lejárt törlés anonimizálás nkt 41' }),
        p('Adatkezelési szabályzat', '/admin/adatkezelesi-szabalyzat', 'A szabályzat és a tájékoztató PDF-je a nyilvános adatkezelési oldalra (Nkt. 43. §)',
          { kulcsszo: 'gdpr adatvédelem adatkezelési szabályzat tájékoztató nkt 43 közzététel' }),
        p('Mobil IdP-címtár', '/admin/idp-regisztracio', 'Az intézmény a mobilos belépés választójában'),
        p('Kapcsolatok', '/admin/kapcsolatok', 'Kapcsolati kód más intézménynek, napló, visszavonás', { kulcsszo: 'kapcsolati kód' }),
        p('Wifi eszközök (MAC)', '/admin/mac-allowlist', 'Engedélyezett eszközök'),
      ] },
      { cim: 'Működés', elemek: [
        p('Frissítés', '/admin/frissites', 'Új verzió telepítése'),
        p('Fájltárolás', '/admin/minio-beallitas', 'MinIO kapcsolat'),
        p('Licenc', '/admin/licenc', 'Az intézmény licencének aktiválása és állapota', { kulcsszo: 'licenc aktiválás kulcs modul korlát előfizetés' }),
        p('Kimenő levelezés', '/admin/smtp-beallitas', 'SMTP-kapcsolat, küldő cím'),
        p('Hibajegy – Redmine kapcsolat', '/admin/hibajegy-beallitas', 'Hová mennek a hibabejelentések'),
        p('Konrád nyuszi (segéd)', '/admin/konrad-beallitas', 'A beépített segéd beállításai'),
        p('Modulok témája', '/admin/modul-tema', 'Klasszikus vagy Fókusz kialakítás, modulonként'),
        p('Tudásbázis közzététele', '/admin/tudasbazis', 'A felhasználói kézikönyv'),
      ] },
      torzsTablak('felulet'),
    ],
  },
];

/**
 *  Az adott modul területei. SULINFORM-ban a fenti 8 terület (a felület régi neve – „Felület”,
 *  „E-napló”… – a kereső kulcsszava), más modulban minden katalógus-csoport egy terület,
 *  egyetlen alcsoporttal.
 */
export function adminTeruletek(mode: string | null | undefined): AdminTerulet[] {
  if (String(mode || '') === 'SULINFORM') return SULINFORM_TERULETEK;
  return adminKatalogus(mode).map((cs) => ({
    kulcs: cs.kulcs, cim: cs.cim, leiras: '', kep: cs.kep,
    alcsoportok: [{
      cim: '',
      elemek: [...cs.elemek, ...(cs.kozvetlen ? [cs.kozvetlen] : [])],
      torzsKategoriak: cs.torzsKategoria ? [cs.torzsKategoria] : [],
    }],
  }));
}

/** A teljességi ellenőrzéshez (a régi SULINFORM-csoportok minden útvonala megvan-e a területekben). */
export const __sulinformRegiUtak = (): string[] => [
  ...SULINFORM_CSOPORTOK.flatMap((cs) => [...cs.elemek, ...(cs.kozvetlen ? [cs.kozvetlen] : [])]),
  ...ALTALANOS_CSOPORTOK.flatMap((cs) => cs.elemek),
].filter((e) => !torzsHelyE(e)).map((e) => (e as AdminElem).ut);
